/* The recent turns, in the shape BOTH readers on the server expect.

   Its own module so it can be tested without dragging in the provider chain, the same
   reason claraPatients lives beside it. Named for turns rather than history because
   ClaraHistory.js is the history dropdown, and two files differing only in case break
   the build on a case insensitive filesystem.

   Two different things read this history and they read different fields. The vitals
   parser reads `query`, the structured form of what Clara understood, so it can resolve
   "and SpO2?" against the last query. The follow-up resolver reads `answer`, the prose,
   so it can turn "tell me in detail" into a question a search index can answer.

   This used to send only `query`, and only for turns that had one. A documents answer
   carries no structured query, so a policy turn was invisible: asked "who may inject
   chemotherapy into an Ommaya reservoir" and then "tell me in detail", Clara received the
   second question with no history at all, searched the index for the literal words "tell
   me in detail", found nothing that could be quoted, and fell back to listing what she
   holds. The conversation was on screen the whole time and none of it was sent.

   Answers are truncated because the resolver only reads the first 400 characters anyway,
   and the whole history rides on every request.
*/
const HISTORY_ANSWER_CHARS = 400

export function conversationHistory(messages = []) {
	const history = []
	for (let i = 0; i < messages.length - 1; i++) {
		const asked = messages[i]
		const replied = messages[i + 1]
		if (asked?.who !== "user" || replied?.who !== "clara") {
			continue
		}
		const turn = {
			question: asked.text,
			answer: (replied.text || "").slice(0, HISTORY_ANSWER_CHARS),
		}
		if (replied.query) {
			turn.query = replied.query
		}
		history.push(turn)
	}
	return history
}


/* A clean one line summary of what Clara understood, for the chip under an answer.

   A clock question shows its clock window ("since 0700 (10 Nov 2025)"), not the 4 hour
   default the parser carries alongside it. The chip read "map summary · 4h" under an
   answer about everything since seven that morning. */
// How a signal is named on the chip. The parse carries the clinician's own word ("map",
// "hr", "sats"), which read as a typo on the chip; the chip says what was measured.
const SIGNAL_NAMES = {
	hr: "Heart rate", "heart rate": "Heart rate", pulse: "Heart rate",
	map: "MAP", "mean arterial pressure": "MAP", bp: "MAP", "blood pressure": "MAP",
	icp: "ICP", "intracranial pressure": "ICP",
	spo2: "SpO2", sats: "SpO2", sat: "SpO2", "oxygen saturation": "SpO2",
	rr: "Respiratory rate", "respiratory rate": "Respiratory rate",
	etco2: "EtCO2", co2: "EtCO2",
	sbp: "Systolic BP", systolic: "Systolic BP", "systolic blood pressure": "Systolic BP",
	dbp: "Diastolic BP", diastolic: "Diastolic BP", "diastolic blood pressure": "Diastolic BP",
}

function signalName(signal) {
	const text = String(signal || "").trim()
	if (!text) return ""
	return SIGNAL_NAMES[text.toLowerCase()] || text.charAt(0).toUpperCase() + text.slice(1)
}

function hoursPhrase(hours) {
	const value = Number(hours)
	if (!Number.isFinite(value) || value <= 0) return null
	if (value < 1) return `last ${Math.round(value * 60)} min`
	if (value % 24 === 0 && value >= 48) return `last ${value / 24} days`
	return `last ${Number(value.toFixed(1))} h`
}

const present = value => value !== null && value !== undefined && value !== "" && value !== "null"

/* The "understood" chip: what Clara measured, in plain words, so a clinician can see at a
   glance that the question was read the way she meant it. It printed the parse raw:
   "map below 65 · 4h", "Heart rate documents null · 4h" (UTSW). Any part that is missing
   is left out rather than printed as "null". */
export function interpChip(query) {
	if (!query) {
		return null
	}
	if (query.mode === "about" || query.mode === "availability" || query.mode === "select_patient") {
		return null // self-describing answers don't need an "understood" chip
	}
	if (query.mode === "documents") {
		// No signal, no window: a policy answer is not a measurement. Falling through
		// rendered whatever residue the parse carried ("Heart rate documents null · 4h").
		return "From your documents"
	}
	const signal = signalName(query.signal)
	const window = query.clock_window?.label || hoursPhrase(query.window_hours)
	const signals = (Array.isArray(query.signals) ? query.signals : []).map(signalName).filter(Boolean)
	let core = ""
	if (query.mode === "summary" && signals.length > 1) {
		core = `${signals.slice(0, -1).join(", ")} and ${signals[signals.length - 1]}`
	} else if (query.mode === "concept") {
		core = present(query.concept) ? `${String(query.concept).charAt(0).toUpperCase()}${String(query.concept).slice(1)} check` : ""
	} else if (query.mode === "summary") {
		core = signal ? `${signal}, average and range` : ""
	} else if (present(query.low) && present(query.high)) {
		const side = query.mode === "outside_range" ? "outside" : "between"
		core = signal ? `${signal} ${side} ${query.low} and ${query.high}` : ""
	} else if ((query.mode === "above" || query.mode === "below") && present(query.value)) {
		core = signal ? `${signal} ${query.mode} ${query.value}` : ""
	}
	// A window on its own says nothing about what was understood.
	return core ? [core, window].filter(Boolean).join(" · ") : null
}

/* The reference lines Clara's chart spec draws: a threshold line, or a range band's two edges.
   The spec says which lines an answer has, so a summary draws none without guessing from its
   placeholder threshold. Null when an answer has no spec (older answers). */
export function viewLines(view) {
	if (!view || !Array.isArray(view.overlays)) {
		return null
	}
	const lines = []
	for (const overlay of view.overlays) {
		if (overlay.type === "line" && overlay.value != null) {
			lines.push(overlay.value)
		} else if (overlay.type === "band" && overlay.low != null && overlay.high != null) {
			lines.push(overlay.low, overlay.high)
		}
	}
	return lines
}

/* An answer about several signals has no single result. Its SVG chart, drawn for older answers and
   Enlarge, shows the first signal, from the spec's series. Null when the first signal has no line. */
export function viewResult(view) {
	const series = view?.series?.[0]
	if (!Array.isArray(series?.trend) || series.trend.length < 2) {
		return null
	}
	return { trend: series.trend, episodes: [], crossings: [], unit: series.unit, query: { start_time: view.window?.start_ms, end_time: view.window?.end_ms } }
}

export function viewSeries(view) {
	if (!Array.isArray(view?.series) || view.series.length < 2) {
		return []
	}
	return view.series
		.filter(item => Array.isArray(item?.trend) && item.trend.length > 1)
		.map(item => ({
			signal: item.signal,
			label: item.unit ? `${item.display} (${item.unit})` : item.display,
			result: { trend: item.trend, episodes: [], crossings: [], unit: item.unit, query: { start_time: view.window?.start_ms, end_time: view.window?.end_ms } },
		}))
}

/* Rebuild a saved conversation's messages for display.

   The stored `chart` lets each answer redraw its SVG over the window it was about, and a
   minimal result.query lets thresholdsOf() find the reference lines. Marked `historical`
   so it uses the SVG, never the live single instance embed.

   `sources` come back with each turn so a reopened chat still shows the passages its
   answers rested on. Nitya, 24 Sep: returning to a chat showed "documents null" where the
   sources had been. The headline and steps drawn above an answer are saved in its chart. */
export function rebuildTurns(turns = []) {
	const rebuilt = []
	for (const turn of turns) {
		rebuilt.push({ who: "user", text: turn.question })
		const pq = turn.parsed_query || null
		const chart = turn.chart || null
		const result = chart && Array.isArray(chart.trend) && chart.trend.length > 1
			? {
				trend: chart.trend,
				episodes: chart.episodes || [],
				crossings: chart.crossings || [],
				unit: chart.unit,
				query: pq ? { low: pq.low, high: pq.high, threshold: pq.value, mode: pq.mode, start_time: chart.start_time, end_time: chart.end_time } : null,
			}
			: null
		const sources = Array.isArray(turn.sources) ? turn.sources : []
		const headline = chart?.headline || null
		const steps = Array.isArray(chart?.steps) ? chart.steps : []
		const headlines = Array.isArray(chart?.headlines) ? chart.headlines : []
		const view = chart?.view || null
		rebuilt.push({ who: "clara", text: turn.answer || "", query: pq, result, view, sources, headline, headlines, steps, historical: true })
	}
	return rebuilt
}
